/**
 * SEO constants and shared meta fragments, mirroring the head output of the
 * main app's react-helmet tags (frontend/src/App.tsx and page Helmets) so
 * crawlers see identical metadata from the SSR pages.
 */

import type {MetaDescriptor} from "react-router";

export const CANONICAL_ORIGIN = "https://www.flux.ai";
export const SITE_NAME = "Flux.ai";
export const TWITTER_HANDLE = "buildwithflux";
export const APP_DESCRIPTION =
    "Go 10x faster from idea to PCB by reducing busy work, never starting from scratch, and keeping your team in sync. Now with the help of Flux AI.";

const LD_APP_DESCRIPTION =
    "Browser-based AI-powered EDA tool for designing printed circuit boards (PCBs). Design schematics, layout PCBs, and collaborate in real-time.";

export function toCanonicalUrl(path: string): string {
    return `${CANONICAL_ORIGIN}${path}`;
}

/** og:site_name / twitter:card / twitter:site shared by every page. */
export function siteMetaDescriptors(): MetaDescriptor[] {
    return [
        {property: "og:site_name", content: SITE_NAME},
        {name: "twitter:card", content: "summary"},
        {name: "twitter:site", content: TWITTER_HANDLE},
    ];
}

const WEBSITE_JSON_LD = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Flux",
    url: "https://flux.ai",
    description: LD_APP_DESCRIPTION,
    potentialAction: {
        "@type": "SearchAction",
        target: {"@type": "EntryPoint", urlTemplate: "https://flux.ai/search?q={search_term_string}"},
        "query-input": "required name=search_term_string",
    },
});

const SOFTWARE_APPLICATION_JSON_LD = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Flux",
    url: "https://flux.ai",
    description: LD_APP_DESCRIPTION,
    applicationCategory: "DesignApplication",
    operatingSystem: "Web",
    offers: [
        {"@type": "Offer", name: "Starter", price: "15", priceCurrency: "USD", billingDuration: "P1M", url: "https://www.flux.ai/p/pricing"},
        {"@type": "Offer", name: "Pro", price: "39", priceCurrency: "USD", billingDuration: "P1M", url: "https://www.flux.ai/p/pricing"},
        {"@type": "Offer", name: "Teams", price: "49", priceCurrency: "USD", billingDuration: "P1M", url: "https://www.flux.ai/p/pricing"},
    ],
});

/** WebSite + SoftwareApplication JSON-LD rendered app-wide (matches frontend/src/App.tsx). */
export function GlobalStructuredData() {
    return (
        <>
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: WEBSITE_JSON_LD}} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: SOFTWARE_APPLICATION_JSON_LD}} />
        </>
    );
}
